export type OpsOrbCanvasLease = {
  epoch: number;
};

export type OpsOrbAnimationFrameLease = {
  start(): void;
  setPaused(paused: boolean): void;
  destroy(): void;
};

export function createOpsOrbAnimationFrameLease(input: {
  requestFrame(callback: () => void): number;
  cancelFrame(frameId: number): void;
  renderFrame(): void;
}): OpsOrbAnimationFrameLease {
  let scheduledFrame: number | null = null;
  let paused = true;
  let destroyed = false;

  const cancelScheduledFrame = () => {
    if (scheduledFrame === null) return;
    input.cancelFrame(scheduledFrame);
    scheduledFrame = null;
  };
  const tick = () => {
    scheduledFrame = null;
    if (destroyed || paused) return;
    scheduledFrame = input.requestFrame(tick);
    input.renderFrame();
  };
  const rearm = () => {
    cancelScheduledFrame();
    if (!destroyed && !paused) scheduledFrame = input.requestFrame(tick);
  };

  return {
    start() {
      if (destroyed) return;
      paused = false;
      rearm();
    },
    setPaused(nextPaused) {
      if (destroyed) return;
      paused = nextPaused;
      // WHY [BOL-ORB-EDGE-BFCACHE-RAF-019]: Edge may freeze/drop a queued RAF
      // while preserving this JS owner, then restore through `pageshow` without
      // a matching visibility edge. A visible resume must therefore rearm even
      // when the old boolean already says unpaused. Cancel-first preserves one
      // loop and the same canvas/context, avoiding both the blank outline and
      // the rejected replacement-canvas white compositor block.
      if (nextPaused) cancelScheduledFrame();
      else rearm();
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      cancelScheduledFrame();
    }
  };
}

export type OpsOrbCanvasLeaseEvent =
  | "hidden"
  | "visible"
  | "replace"
  | "context-lost"
  | "context-restored";

export function nextOpsOrbCanvasLease(
  previous: OpsOrbCanvasLease,
  event: OpsOrbCanvasLeaseEvent
): OpsOrbCanvasLease {
  // WHY [BOL-ORB-FOREGROUND-LEASE-018]: the Orb canvas overscans its 88px stage
  // so its particles can breathe. Replacing that transparent WebGL surface on
  // every Android app switch briefly exposed a new opaque compositor layer as
  // the reported oversized white block. Visibility now pauses/resizes the one
  // renderer; native WebGL loss restores that same canvas. Only a confirmed
  // fatal initialization may allocate a new DOM/context identity.
  if (event !== "replace") return previous;
  return { epoch: previous.epoch + 1 };
}
